<app-vault-module-shell
  moduleLabel="Preservación Digital"
  [accessDenied]="accessDenied()"
  [loadError]="loadError()"
  [loadErrorTitle]="loadErrorTitle()"
>
<section class="preservation">
  <app-page-header
    title="Preservación Digital"
    subtitle="Monitorea el estado de preservación y archivos con riesgo."
    icon="Shield"
    [actions]="headerActions()"
    (actionClick)="onHeaderAction($event)"
  />

  <div class="preservation__oais">
    <app-oais-status-indicator [stage]="oaisStage()" />
  </div>

  @if (loading()) {
    <div class="loading-container">
      <lib-empty message="Cargando preservación" description="Espere por favor..." />
    </div>
  }
  @if (error()) {
    <app-vault-page-alert
      type="error"
      title="Error"
      [message]="error()"
      (closed)="error.set(null)"
    />
  }

  <fieldset class="preservation__fieldset">
    <legend>Diagnóstico y reparación</legend>

    <div class="preservation__create-action">
      <lib-button
        variant="secondary"
        prefixIcon="SearchCheck"
        [loading]="diagnosing()"
        [disabled]="diagnosing() || repairing()"
        (clicked)="diagnoseFile()"
      >
        Diagnosticar
      </lib-button>
      <lib-button
        variant="primary"
        prefixIcon="FolderSync"
        [loading]="repairing()"
        [disabled]="repairing() || diagnosing()"
        (clicked)="repairSelectedIssues()"
      >
        Reparar seleccionados ({{ selectedIssueCount() }})
      </lib-button>
    </div>

    <app-grid-form preset="responsive" wrapperClass="preservation__form-grid">
      <app-grid-form-item [span]="'full'" class="preservation-field">
        <app-field-label
          label="Ruta de archivo"
          [required]="true"
          tooltip="Ruta absoluta o relativa del documento en repositorio para diagnóstico."
        />
        <lib-input
          label=""
          placeholder="Ej: /tenant/docs/contrato-001.pdf"
          [required]="true"
          [value]="diagnosticPath()"
          (valueChange)="onDiagnosticPathChange($event)"
        />
      </app-grid-form-item>
    </app-grid-form>

    @if (diagnosticError()) {
      <p class="preservation__error">{{ diagnosticError() }}</p>
    }
    @if (diagnosticInfo()) {
      <p class="preservation__info">{{ diagnosticInfo() }}</p>
    }

    @if (issueOptions().length > 0) {
      <div class="preservation__issues-toolbar">
        <lib-button variant="secondary" (clicked)="selectAllIssues(true)">
          Seleccionar todo
        </lib-button>
        <lib-button variant="secondary" (clicked)="selectAllIssues(false)">
          Limpiar seleccion
        </lib-button>
      </div>

      <div class="preservation__issues-list">
        @for (option of issueOptions(); track option.issue) {
          <lib-checkbox
            [label]="option.issue"
            [(checked)]="option.selected"
          />
        }
      </div>
    }
  </fieldset>

  @if (diagnosedIssues().length > 0 || repairedIssues().length > 0 || pendingIssues().length > 0) {
    <div class="preservation__diagnostic-result">
      <h3>Resultado del proceso</h3>

      @if (diagnosedIssues().length > 0) {
        <div>
          <h4>Problemas detectados</h4>
          <ul>
            @for (issue of diagnosedIssues(); track issue) {
              <li>{{ issue }}</li>
            }
          </ul>
        </div>
      }

      @if (repairedIssues().length > 0) {
        <div>
          <h4>Problemas reparados</h4>
          <ul>
            @for (issue of repairedIssues(); track issue) {
              <li>{{ issue }}</li>
            }
          </ul>
        </div>
      }

      @if (pendingIssues().length > 0) {
        <div>
          <h4>Problemas pendientes</h4>
          <ul>
            @for (issue of pendingIssues(); track issue) {
              <li>{{ issue }}</li>
            }
          </ul>
        </div>
      }
    </div>
  }

  @if (status(); as st) {
    <div class="preservation__summary-grid">
      <article class="preservation__summary-card">
        <h4>Total de ingestas</h4>
        <p>{{ st.total_files }}</p>
        <span class="preservation__summary-card-hint">Paquetes AIP (.zip) preservados</span>
      </article>
      @for (card of summaryCards(); track card.key) {
        <article class="preservation__summary-card">
          <h4>{{ card.label }}</h4>
          <p>{{ card.value }}</p>
          <lib-badge [text]="card.label" [variant]="card.variant" size="small" />
        </article>
      }
    </div>
  }

  <div class="preservation__table-wrap">
    <h3>Archivos críticos</h3>
    <lib-results-counter
      label="Mostrando"
      [current]="criticalFiles().length"
      [total]="criticalTotal()"
      singularLabel="registro"
      pluralLabel="registros"
      separatorLabel="de"
      size="small"
    />
    <lib-table
      [config]="criticalTableConfig()"
      [data]="criticalFiles()"
      [loading]="loading()"
      [pageSize]="tablePageSize()"
      [pageSizeOptions]="tablePageSizeOptions"
      (action)="onTableAction($event)"
    >
      <ng-template #cellTemplate let-row let-column="column">
        @if (column.key === 'statusLabel') {
          <app-preservation-status-badge [status]="row.status" />
        }
      </ng-template>
    </lib-table>
  </div>

  <div class="preservation__table-wrap">
    <h3>Archivos en atención</h3>
    <lib-results-counter
      label="Mostrando"
      [current]="attentionFiles().length"
      [total]="attentionTotal()"
      singularLabel="registro"
      pluralLabel="registros"
      separatorLabel="de"
      size="small"
    />
    <lib-table
      [config]="attentionTableConfig()"
      [data]="attentionFiles()"
      [loading]="loading()"
      [pageSize]="tablePageSize()"
      [pageSizeOptions]="tablePageSizeOptions"
      (action)="onTableAction($event)"
    >
      <ng-template #cellTemplate let-row let-column="column">
        @if (column.key === 'statusLabel') {
          <app-preservation-status-badge [status]="row.status" />
        }
      </ng-template>
    </lib-table>
  </div>

  <div class="preservation__table-wrap">
    <h3>Archivos óptimos</h3>
    <lib-results-counter
      label="Mostrando"
      [current]="optimalFiles().length"
      [total]="optimalTotal()"
      singularLabel="registro"
      pluralLabel="registros"
      separatorLabel="de"
      size="small"
    />
    @if (optimalDetailHint(); as hint) {
      <p class="preservation__info">{{ hint }}</p>
    }
    @if (optimalEndpointUnavailable()) {
      <p class="preservation__error">
        No se pudo cargar el detalle de archivos óptimos desde <code>/api/preservation/optimal</code>.
        {{ optimalEndpointError() || '' }}
      </p>
    }
    <lib-table
      [config]="optimalTableConfig()"
      [data]="optimalFiles()"
      [loading]="loading()"
      [pageSize]="tablePageSize()"
      [pageSizeOptions]="tablePageSizeOptions"
      (action)="onTableAction($event)"
    >
      <ng-template #cellTemplate let-row let-column="column">
        @if (column.key === 'statusLabel') {
          <app-preservation-status-badge [status]="row.status" />
        }
      </ng-template>
    </lib-table>
  </div>

  @if (
    !loading() &&
    !error() &&
    criticalFiles().length === 0 &&
    attentionFiles().length === 0 &&
    optimalFiles().length === 0 &&
    (status()?.status_summary?.optimal ?? 0) === 0
  ) {
    <lib-empty
      icon="Shield"
      message="Sin archivos en riesgo"
      description="No hay archivos críticos ni en atención para el tenant activo."
    />
    <div class="vault-empty-cta">
      <lib-button variant="secondary" type="button" prefixIcon="RefreshCcw" (clicked)="loadDashboard()">
        Actualizar
      </lib-button>
    </div>
  }

  @if (diagnosticResultDialog(); as dialog) {
    <div class="preservation__modal-backdrop" role="presentation">
      <div
        class="preservation__modal"
        [class.preservation__modal--error]="dialog.kind === 'error'"
        role="dialog"
        [attr.aria-modal]="true"
        [attr.aria-labelledby]="'preservation-result-title'"
      >
        <h2 class="preservation__modal-title" id="preservation-result-title">{{ dialog.title }}</h2>
        <p class="preservation__modal-message">{{ dialog.message }}</p>
        <div class="preservation__modal-actions">
          <lib-button variant="primary" (clicked)="closeDiagnosticResultDialog()">Aceptar</lib-button>
        </div>
      </div>
    </div>
  }
</section>
</app-vault-module-shell>
